import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Form, Modal, Spinner, Table, Card, Row, Col, Pagination, InputGroup, Tabs, Tab } from "react-bootstrap";
import { Smartphone, ShieldCheck, Search, UserCheck } from 'lucide-react';
import api, {
  getTenantUsers,
  createTenantUser,
  updateTenantUser,
  resetTenantUserPassword
} from "../../api/api";
import { showToast } from '../../utils/toast';
import { getErrorMessage } from '../../utils/errors';

const ROLE_OPTIONS = ["sme", "qa", "media", "pr", "id", "faculty", "coe"];
const STATUS_OPTIONS = ["active", "inactive"];

const TenantUserManagement = ({ tenantSlug, onChanged }) => {
  const [activeTab, setActiveTab] = useState('studio');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Studio State
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userPage, setUserPage] = useState(1);
  
  // Classroom State
  const [classroomUsers, setClassroomUsers] = useState([]);
  const [classroomSearch, setClassroomSearch] = useState('');
  const [classroomPage, setClassroomPage] = useState(1);
  
  const itemsPerPage = 10;

  // Studio Modals
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [selected, setSelected] = useState(null);
  const [createForm, setCreateForm] = useState({ email: "", password: "", role: "sme" });
  const [editForm, setEditForm] = useState({ email: "", role: "sme", status: "active" });
  const [resetForm, setResetForm] = useState({ password: "" });

  // Classroom Modals
  const [showClassroomEdit, setShowClassroomEdit] = useState(false);
  const [showClassroomReset, setShowClassroomReset] = useState(false);
  const [classroomEditForm, setClassroomEditForm] = useState({ first_name: "", last_name: "", role: "faculty", external_id: "", mentoring_enabled: false });
  const [classroomResetForm, setClassroomResetForm] = useState({ password: "" });

  // Governance & Promote
  const [govSettings, setGovSettings] = useState({ allow_public_email: false, allowed_domains: "", require_2fa: true });
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [promoteForm, setPromoteForm] = useState({ first_name: "", last_name: "", role: "faculty", external_id: "" });

  // --- LOAD DATA ---
  const load = async () => {
    try {
      setError("");
      setBusy(true);
      const [userRes, classroomRes, govRes] = await Promise.all([
        getTenantUsers(tenantSlug),
        api.get(`/tenant/${tenantSlug}/classroom/users`),
        api.get(`/tenant/${tenantSlug}/settings`)
      ]);
      
      setUsers(userRes.data || []);
      setClassroomUsers(classroomRes.data || []);
      setGovSettings({
        ...govRes.data,
        allowed_domains: govRes.data.allowed_domains?.join(', ') || ""
      });
    } catch (e) { setError(getErrorMessage(e, "Failed to load management data")); } finally { setBusy(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- load() is re-created every render; only tenantSlug should retrigger this
  useEffect(() => { if (tenantSlug) load(); }, [tenantSlug]);

  const saveGovernance = async () => {
    try {
      setBusy(true);
      const payload = {
        ...govSettings,
        allowed_domains: govSettings.allowed_domains.split(',').map(d => d.trim().toLowerCase()).filter(d => d)
      };
      await api.patch(`/tenant/${tenantSlug}/settings`, payload);
      showToast("Institutional Governance Rules Applied.", 'info');
    } catch (e) { setError("Failed to update governance settings."); } finally { setBusy(false); }
  };

  // --- STUDIO ACTIONS ---
  const openEdit = (u) => { setSelected(u); setEditForm({ email: u.email, role: u.role, status: u.status || "active" }); setShowEdit(true); };
  const openReset = (u) => { setSelected(u); setResetForm({ password: "" }); setShowReset(true); };
  
  const handleCreate = async () => {
    if (!createForm.email || !createForm.password) return setError("Email and password are required");
    try {
      setBusy(true);
      await createTenantUser(tenantSlug, createForm);
      setShowCreate(false);
      setCreateForm({ email: "", password: "", role: "sme" });
      await load();
      onChanged?.();
    } catch (e) { setError(getErrorMessage(e, "Failed to create user")); } finally { setBusy(false); }
  };

  const handleEdit = async () => {
    if (!selected) return;
    try {
      setBusy(true);
      await updateTenantUser(tenantSlug, selected.id, editForm);
      setShowEdit(false);
      setSelected(null);
      await load();
      onChanged?.();
    } catch (e) { setError(getErrorMessage(e, "Failed to update user")); } finally { setBusy(false); }
  };

  const handleReset = async () => {
    if (!selected || !resetForm.password) return setError("Password required");
    try {
      setBusy(true);
      await resetTenantUserPassword(tenantSlug, selected.id, resetForm);
      setShowReset(false);
      setSelected(null);
      showToast("Password reset successfully.", 'success');
    } catch (e) { setError(getErrorMessage(e, "Failed to reset password")); } finally { setBusy(false); }
  };

  // --- 🌟 NEW: Clean toggle replacing the old delete function ---
  const toggleStudioStatus = async (u) => {
    const newStatus = (u.status || "active") === "active" ? "inactive" : "active";
    if (!window.confirm(`Are you sure you want to mark this studio user as ${newStatus}?`)) return;
    try {
      setBusy(true);
      // We use updateTenantUser to cleanly patch the status without triggering a delete
      await updateTenantUser(tenantSlug, u.id, { status: newStatus });
      await load();
      onChanged?.();
    } catch (e) {
      setError(getErrorMessage(e, "Failed to update user status"));
    } finally {
      setBusy(false);
    }
  };

  // --- CLASSROOM ACTIONS ---
  const openPromote = (u) => { setSelected(u); setPromoteForm({ first_name: u.first_name || "", last_name: u.last_name || "", role: "faculty", external_id: "" }); setShowPromoteModal(true); };
  
  const handlePromotion = async () => {
    try {
        setBusy(true);
        await api.post(`/tenant/${tenantSlug}/classroom/users`, {
            email: selected.email, 
            ...promoteForm,
            sso_source: 'internal'
        });
        setShowPromoteModal(false);
        await load();
        onChanged?.();
        showToast("User successfully authorized for Classroom.", 'success');
    } catch (err) { setError(getErrorMessage(err, "Promotion failed.")); } finally { setBusy(false); }
  };

  const openClassroomEdit = (u) => {
      setSelected(u);
      setClassroomEditForm({ first_name: u.first_name || '', last_name: u.last_name || '', role: u.role || 'faculty', external_id: u.external_id || '', mentoring_enabled: !!u.mentoring_enabled });
      setShowClassroomEdit(true);
  };

  const handleClassroomEdit = async () => {
      try {
          setBusy(true);
          await api.patch(`/tenant/${tenantSlug}/classroom/users/${selected.id}`, classroomEditForm);
          setShowClassroomEdit(false);
          await load();
          onChanged?.();
      } catch (err) { setError("Failed to update classroom user."); } finally { setBusy(false); }
  };

  const openClassroomReset = (u) => {
      setSelected(u);
      setClassroomResetForm({ password: "" });
      setShowClassroomReset(true);
  };

  const handleClassroomReset = async () => {
      if (!classroomResetForm.password) return setError("Password required");
      try {
          setBusy(true);
          await api.patch(`/tenant/${tenantSlug}/classroom/users/${selected.id}/reset-password`, classroomResetForm);
          setShowClassroomReset(false);
          showToast("Classroom password reset successfully.", 'success');
      } catch (err) { setError("Failed to reset classroom password."); } finally { setBusy(false); }
  };

  const toggleClassroomStatus = async (user) => {
    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    if (!window.confirm(`Are you sure you want to mark this classroom user as ${newStatus}?`)) return;
    try {
        setBusy(true);
        await api.patch(`/tenant/${tenantSlug}/classroom/users/${user.id}/status`, { status: newStatus });
        await load();
        onChanged?.();
    } catch (e) { setError("Failed to change classroom status"); } finally { setBusy(false); }
  };

  // --- FILTERS & PAGINATION ---
  const filteredStudioUsers = useMemo(() => users.filter(u => 
      u.email.toLowerCase().includes(userSearch.toLowerCase()) || u.role.toLowerCase().includes(userSearch.toLowerCase())
  ), [users, userSearch]);
  const paginatedStudio = useMemo(() => filteredStudioUsers.slice((userPage - 1) * itemsPerPage, userPage * itemsPerPage), [filteredStudioUsers, userPage]);

  const filteredClassroomUsers = useMemo(() => classroomUsers.filter(u => 
    u.email.toLowerCase().includes(classroomSearch.toLowerCase()) || 
    (u.first_name && u.first_name.toLowerCase().includes(classroomSearch.toLowerCase()))
  ), [classroomUsers, classroomSearch]);
  const paginatedClassroom = useMemo(() => filteredClassroomUsers.slice((classroomPage - 1) * itemsPerPage, classroomPage * itemsPerPage), [filteredClassroomUsers, classroomPage]);

  return (
    <>
      <Card className="border-0 shadow-sm mb-4 bg-light">
        <Card.Header className="bg-white py-3 d-flex align-items-center gap-2">
          <ShieldCheck className="text-primary" size={20} />
          <h6 className="mb-0 fw-bold">Institutional Access Governance</h6>
        </Card.Header>
        <Card.Body className="p-4">
          <Row className="g-4">
            <Col md={6}>
              <Form.Label className="small fw-bold text-muted uppercase">Domain Control</Form.Label>
              <Form.Check type="switch" label="Allow Public Domains (Gmail/Outlook)" checked={govSettings.allow_public_email} onChange={(e) => setGovSettings({...govSettings, allow_public_email: e.target.checked})} className="mb-3" />
              <Form.Label className="small fw-bold text-muted">Whitelisted Domains</Form.Label>
              <Form.Control size="sm" value={govSettings.allowed_domains} onChange={(e) => setGovSettings({...govSettings, allowed_domains: e.target.value})} />
            </Col>
            <Col md={6} className="border-start ps-4">
              <Form.Label className="small fw-bold text-muted uppercase">Security Policies</Form.Label>
              <Form.Check type="switch" label="Enforce 2FA for Faculty Onboarding" checked={govSettings.require_2fa} onChange={(e) => setGovSettings({...govSettings, require_2fa: e.target.checked})} />
            </Col>
          </Row>
          <div className="text-end mt-3">
             <Button variant="primary" size="sm" onClick={saveGovernance} disabled={busy}>{busy ? <Spinner size="sm" aria-hidden="true" /> : "Save Governance Rules"}</Button>
          </div>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger" role="alert" dismissible onClose={() => setError("")} className="small py-2">{error}</Alert>}

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3 custom-tabs">
        
        {/* --- STUDIO TAB --- */}
        <Tab eventKey="studio" title={<span>🎨 Studio Roster ({users.length})</span>}>
            <div className="d-flex justify-content-between align-items-end mb-3 mt-3">
                <InputGroup size="sm" style={{ width: '250px' }}>
                    <InputGroup.Text className="bg-white"><Search size={14} /></InputGroup.Text>
                    <Form.Control placeholder="Search studio staff..." value={userSearch} onChange={(e) => {setUserSearch(e.target.value); setUserPage(1)}} />
                </InputGroup>
                <Button variant="primary" size="sm" className="rounded-pill px-3" onClick={() => setShowCreate(true)} disabled={busy}>+ Add Staff</Button>
            </div>

            <Table hover responsive className="bg-white shadow-sm rounded overflow-hidden align-middle">
                <thead className="bg-light x-small text-uppercase fw-bold text-muted">
                    <tr><th>User Identity</th><th>Role</th><th>Status</th><th>Classroom Authorization</th><th scope="col" className="text-end">Management</th></tr>
                </thead>
                <tbody className="small">
                    {paginatedStudio.map((u) => (
                        <tr key={u.id}>
                            <td><div className="fw-bold">{u.email}</div></td>
                            <td><Badge bg="light" text="dark" className="border text-uppercase">{u.role}</Badge></td>
                            <td><Badge bg={(u.status || "active") === "active" ? "success" : "secondary"} className="rounded-pill">{u.status || "active"}</Badge></td>
                            <td>
                                {u.is_classroom_user ? (
                                    <Badge bg="success" className="bg-opacity-10 text-success border border-success px-2 py-1"><ShieldCheck size={12} className="me-1" /> Authorized</Badge>
                                ) : (
                                    <Button size="sm" variant="outline-info" className="py-0 px-2 x-small rounded-pill" onClick={() => openPromote(u)} disabled={busy || (u.status || "active") === "inactive"}>
                                        <Smartphone size={10} className="me-1" /> Promote
                                    </Button>
                                )}
                            </td>
                            <td className="text-end">
                                <Button size="sm" variant="light" className="text-primary me-1" onClick={() => openEdit(u)} disabled={busy}>Edit</Button>
                                <Button size="sm" variant="light" className="text-warning me-1" onClick={() => openReset(u)} disabled={busy}>Reset</Button>
                                <Button 
                                  size="sm" 
                                  variant={u.status === 'active' ? "outline-danger" : "outline-success"} 
                                  className="rounded-pill py-0 px-2 x-small" 
                                  onClick={() => toggleStudioStatus(u)} 
                                  disabled={busy}
                              >
                                  <UserCheck size={12} className="me-1" /> 
                                  {u.status === 'active' ? 'Deactivate' : 'Activate'}
                              </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            <div className="d-flex justify-content-between align-items-center mt-3">
                <small className="text-muted">Showing {paginatedStudio.length} of {filteredStudioUsers.length}</small>
                <Pagination size="sm"><Pagination.Prev disabled={userPage===1} onClick={()=>setUserPage(p=>p-1)}/><Pagination.Next disabled={userPage*itemsPerPage>=filteredStudioUsers.length} onClick={()=>setUserPage(p=>p+1)}/></Pagination>
            </div>
        </Tab>

        {/* --- CLASSROOM TAB --- */}
        <Tab eventKey="classroom" title={<span>🎓 Classroom Roster ({classroomUsers.length})</span>}>
            <div className="d-flex justify-content-between align-items-end mb-3 mt-3">
                <InputGroup size="sm" style={{ width: '250px' }}>
                    <InputGroup.Text className="bg-white"><Search size={14} /></InputGroup.Text>
                    <Form.Control placeholder="Search faculty & staff..." value={classroomSearch} onChange={(e) => {setClassroomSearch(e.target.value); setClassroomPage(1)}} />
                </InputGroup>
            </div>

            <Table hover responsive className="bg-white shadow-sm rounded overflow-hidden align-middle">
                <thead className="bg-light x-small text-uppercase fw-bold text-muted">
                    <tr><th>Faculty Identity</th><th>Role</th><th>External ID</th><th>Status</th><th scope="col" className="text-end">Management</th></tr>
                </thead>
                <tbody className="small">
                    {paginatedClassroom.length === 0 ? (
                        <tr><td colSpan={5} className="text-center py-4 text-muted">No classroom users found.</td></tr>
                    ) : (
                        paginatedClassroom.map((c) => (
                            <tr key={c.id}>
                                <td>
                                    <div className="fw-bold">{c.first_name} {c.last_name}</div>
                                    <div className="x-small text-muted">{c.email}</div>
                                </td>
                                <td><Badge bg="info" className="text-uppercase bg-opacity-10 text-info border border-info">{c.role}</Badge></td>
                                <td>{c.external_id ? <code className="text-muted bg-light px-2 py-1 rounded">{c.external_id}</code> : <span className="text-muted x-small">N/A</span>}</td>
                                <td><Badge bg={c.status === "active" ? "success" : "danger"} className="rounded-pill">{c.status}</Badge></td>
                                <td className="text-end">
                                    <Button size="sm" variant="light" className="text-primary me-1" onClick={() => openClassroomEdit(c)} disabled={busy}>Edit</Button>
                                    <Button size="sm" variant="light" className="text-warning me-1" onClick={() => openClassroomReset(c)} disabled={busy}>Reset</Button>
                                    <Button size="sm" variant={c.status === 'active' ? "outline-danger" : "outline-success"} className="rounded-pill py-0 px-2 x-small" onClick={() => toggleClassroomStatus(c)} disabled={busy}>
                                        <UserCheck size={12} className="me-1" /> 
                                        {c.status === 'active' ? 'Revoke Access' : 'Restore Access'}
                                    </Button>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </Table>
            <div className="d-flex justify-content-between align-items-center mt-3">
                <small className="text-muted">Showing {paginatedClassroom.length} of {filteredClassroomUsers.length}</small>
                <Pagination size="sm"><Pagination.Prev disabled={classroomPage===1} onClick={()=>setClassroomPage(p=>p-1)}/><Pagination.Next disabled={classroomPage*itemsPerPage>=filteredClassroomUsers.length} onClick={()=>setClassroomPage(p=>p+1)}/></Pagination>
            </div>
        </Tab>
      </Tabs>

      {/* --- STUDIO MODALS --- */}
      <Modal show={showCreate} onHide={() => !busy && setShowCreate(false)} backdrop="static">
        <Modal.Header closeButton={!busy}><Modal.Title>Add Studio User</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2"><Form.Label>Email</Form.Label><Form.Control value={createForm.email} onChange={(e) => setCreateForm(s => ({ ...s, email: e.target.value }))} disabled={busy} /></Form.Group>
          <Form.Group className="mb-2"><Form.Label>Role</Form.Label><Form.Select value={createForm.role} onChange={(e) => setCreateForm(s => ({ ...s, role: e.target.value }))} disabled={busy}>{ROLE_OPTIONS.map(r => <option key={r} value={r}>{r.toUpperCase()}</option>)}</Form.Select></Form.Group>
          <Form.Group className="mb-2"><Form.Label>Temporary Password</Form.Label><Form.Control type="password" value={createForm.password} onChange={(e) => setCreateForm(s => ({ ...s, password: e.target.value }))} disabled={busy} /></Form.Group>
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowCreate(false)} disabled={busy}>Cancel</Button><Button onClick={handleCreate} disabled={busy}>{busy ? "Saving..." : "Create"}</Button></Modal.Footer>
      </Modal>

      <Modal show={showEdit} onHide={() => !busy && setShowEdit(false)} backdrop="static">
        <Modal.Header closeButton={!busy}><Modal.Title>Edit Studio User</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2"><Form.Label>Email</Form.Label><Form.Control value={editForm.email} onChange={(e) => setEditForm(s => ({ ...s, email: e.target.value }))} disabled={busy} /></Form.Group>
          <Form.Group className="mb-2"><Form.Label>Role</Form.Label><Form.Select value={editForm.role} onChange={(e) => setEditForm(s => ({ ...s, role: e.target.value }))} disabled={busy}>{ROLE_OPTIONS.map(r => <option key={r} value={r}>{r.toUpperCase()}</option>)}</Form.Select></Form.Group>
          <Form.Group className="mb-2"><Form.Label>Status</Form.Label><Form.Select value={editForm.status} onChange={(e) => setEditForm(s => ({ ...s, status: e.target.value }))} disabled={busy}>{STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}</Form.Select></Form.Group>
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowEdit(false)} disabled={busy}>Cancel</Button><Button onClick={handleEdit} disabled={busy}>{busy ? "Saving..." : "Save Changes"}</Button></Modal.Footer>
      </Modal>

      <Modal show={showReset} onHide={() => !busy && setShowReset(false)} backdrop="static">
        <Modal.Header closeButton={!busy}><Modal.Title>Reset Studio Password</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2"><Form.Label>New Password</Form.Label><Form.Control type="password" value={resetForm.password} onChange={(e) => setResetForm({ password: e.target.value })} disabled={busy} /></Form.Group>
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowReset(false)} disabled={busy}>Cancel</Button><Button onClick={handleReset} disabled={busy}>{busy ? "Resetting..." : "Reset"}</Button></Modal.Footer>
      </Modal>

      <Modal show={showPromoteModal} onHide={() => setShowPromoteModal(false)}>
        <Modal.Header closeButton><Modal.Title>Authorize Classroom Access</Modal.Title></Modal.Header>
        <Modal.Body>
            <Row>
                <Col><Form.Label className="small fw-bold">First Name</Form.Label><Form.Control value={promoteForm.first_name} onChange={e => setPromoteForm({...promoteForm, first_name: e.target.value})} /></Col>
                <Col><Form.Label className="small fw-bold">Last Name</Form.Label><Form.Control value={promoteForm.last_name} onChange={e => setPromoteForm({...promoteForm, last_name: e.target.value})} /></Col>
            </Row>
            <Form.Group className="mt-3">
                <Form.Label className="small fw-bold">Classroom Role</Form.Label>
                <Form.Select value={promoteForm.role} onChange={e => setPromoteForm({...promoteForm, role: e.target.value})}>
                    <option value="faculty">Faculty / Professor</option>
                    <option value="ta">Teaching Assistant (TA)</option>
                    <option value="student_lead">Student Lead</option>
                </Form.Select>
            </Form.Group>
            <Form.Group className="mt-3">
                <Form.Label className="small fw-bold">External ID</Form.Label>
                <Form.Control value={promoteForm.external_id} placeholder="e.g. TAU-10293" onChange={e => setPromoteForm({...promoteForm, external_id: e.target.value})} />
            </Form.Group>
        </Modal.Body>
        <Modal.Footer><Button variant="primary" onClick={handlePromotion} disabled={busy}>{busy ? <Spinner size="sm" aria-hidden="true" /> : "Grant Access"}</Button></Modal.Footer>
      </Modal>

      {/* --- CLASSROOM MODALS --- */}
      <Modal show={showClassroomEdit} onHide={() => !busy && setShowClassroomEdit(false)}>
        <Modal.Header closeButton><Modal.Title>Edit Classroom Profile</Modal.Title></Modal.Header>
        <Modal.Body>
            <Row>
                <Col><Form.Label className="small fw-bold">First Name</Form.Label><Form.Control value={classroomEditForm.first_name} onChange={e => setClassroomEditForm({...classroomEditForm, first_name: e.target.value})} disabled={busy} /></Col>
                <Col><Form.Label className="small fw-bold">Last Name</Form.Label><Form.Control value={classroomEditForm.last_name} onChange={e => setClassroomEditForm({...classroomEditForm, last_name: e.target.value})} disabled={busy}/></Col>
            </Row>
            <Form.Group className="mt-3">
                <Form.Label className="small fw-bold">Classroom Role</Form.Label>
                <Form.Select value={classroomEditForm.role} onChange={e => setClassroomEditForm({...classroomEditForm, role: e.target.value})} disabled={busy}>
                    <option value="faculty">Faculty / Professor</option>
                    <option value="ta">Teaching Assistant (TA)</option>
                    <option value="student_lead">Student Lead</option>
                </Form.Select>
            </Form.Group>
            <Form.Group className="mt-3">
                <Form.Label className="small fw-bold">External ID</Form.Label>
                <Form.Control value={classroomEditForm.external_id} onChange={e => setClassroomEditForm({...classroomEditForm, external_id: e.target.value})} disabled={busy} />
            </Form.Group>
            <Form.Group className="mt-3">
                <Form.Check
                    type="checkbox"
                    id="mentoring-enabled-toggle"
                    label="Eligible for 1:1 student mentoring bookings"
                    checked={classroomEditForm.mentoring_enabled}
                    onChange={e => setClassroomEditForm({...classroomEditForm, mentoring_enabled: e.target.checked})}
                    disabled={busy}
                />
            </Form.Group>
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowClassroomEdit(false)}>Cancel</Button><Button variant="primary" onClick={handleClassroomEdit} disabled={busy}>{busy ? "Saving..." : "Save Changes"}</Button></Modal.Footer>
      </Modal>

      <Modal show={showClassroomReset} onHide={() => !busy && setShowClassroomReset(false)}>
        <Modal.Header closeButton><Modal.Title>Reset Classroom Password</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2"><Form.Label>New Password</Form.Label><Form.Control type="password" value={classroomResetForm.password} onChange={(e) => setClassroomResetForm({ password: e.target.value })} disabled={busy} /></Form.Group>
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowClassroomReset(false)}>Cancel</Button><Button variant="primary" onClick={handleClassroomReset} disabled={busy}>{busy ? "Resetting..." : "Reset Password"}</Button></Modal.Footer>
      </Modal>
    </>
  );
};

export default TenantUserManagement;